import { useDateRangeFilter } from "@/components/observe/useDateRangeFilter";
import { useOrganization, useProject, useSession } from "@/contexts/Auth";
import { useRBAC } from "@/hooks/useRBAC";
import { useGramContext } from "@gram/client/react-query/_context.js";
import { useQueries, useQuery } from "@tanstack/react-query";
import type { UserSummary } from "@gram/client/models/components/usersummary.js";
import {
  fetchIdentityPeers,
  identityPeersQueryKey,
  type IdentityRosterUserType,
  mergeUserSummaries,
} from "./identityPeers";

import { fetchIdentityRoster, identityRosterQueryKey } from "./identityRoster";
import type { AccessMember } from "@gram/client/models/components/accessmember.js";
import type { IdentityModel } from "@gram/client/models/components/identitymodel.js";
import { useAuditLogs } from "@gram/client/react-query/auditLogs.js";
import { useMembers } from "@gram/client/react-query/members.js";
import { useChallenges } from "@gram/client/react-query/challenges.js";
import { useListChats } from "@gram/client/react-query/listChats.js";
import { useManagedDevices } from "@gram/client/react-query/managedDevices.js";
import {
  buildGetUserMetricsSummaryQuery,
  useGetUserMetricsSummary,
} from "@gram/client/react-query/getUserMetricsSummary.js";
import { buildRiskUserBreakdownQuery } from "@gram/client/react-query/riskUserBreakdown.js";
import type { RiskOverviewCategory } from "@gram/client/models/components/riskoverviewcategory.js";
import type { RiskRuleBreakdownEntry } from "@gram/client/models/components/riskrulebreakdownentry.js";
import type { RiskUserBreakdownResult } from "@gram/client/models/components/riskuserbreakdownresult.js";
import { useShadowMCPInventoryServersForUser } from "@gram/client/react-query/shadowMCPInventoryServersForUser.js";
import { useIdentityAccess } from "@gram/client/react-query/identityAccess.js";

const OFF = { throwOnError: false } as const;

/** What access.listShadowMCPInventoryServersForUser accepts in one request. */
const MAX_USER_KEYS = 200;

/** The part of a query a retry button needs. */
type RetryableQuery = { isError: boolean; refetch: () => unknown };

/**
 * A retry that re-runs only the reads which actually failed.
 *
 * Several queries on these pages are held behind `enabled` — no Speakeasy user id,
 * no chat:read, no org:admin — and `refetch()` ignores `enabled` and fires
 * anyway. A retry button that called it blindly would ask for audit logs with
 * no actor filter, or for chats the caller may not filter by user, and render
 * either under this identity's name. A query that is disabled never reaches an
 * error, so having errored is itself proof it was allowed to run.
 */
export function retryFailed(...queries: RetryableQuery[]): () => void {
  return () => {
    for (const query of queries) {
      if (query.isError) void query.refetch();
    }
  };
}

/**
 * The window every identity panel reads, kept in the URL so a link to a
 * sub-page carries the range the sender was looking at.
 */
export function useIdentityWindow(): { from: Date; to: Date } {
  const { from, to } = useDateRangeFilter();
  return { from, to };
}

/**
 * The project the telemetry panels read.
 *
 * The page lives under a project, so this is the route's project rather than a
 * filter of its own. Usage, chats, cost and risk are all recorded per project,
 * and the segment in the address is what says which slice is on screen.
 */
export function useIdentityProject(): { slug: string; id: string } {
  const project = useProject();
  return { slug: project.slug, id: project.id };
}

/**
 * Whether telemetry can be asked about this identity at all.
 *
 * The summary endpoint keys on a Speakeasy user id or an agent-reported id, and an
 * identity carrying neither — an api-key subject, say — is not a subject it
 * can answer for. No request is made, so the tiles have nothing to show and
 * must say that rather than stand at zero.
 */
export function hasMetricsSubject(identity: IdentityModel): boolean {
  return !!(identity.userIds[0] || identity.externalUserIds[0]);
}

/**
 * Telemetry keys usage on either the Speakeasy user id or the id an agent reported,
 * and the endpoint takes exactly one of them, so prefer the directory user and
 * fall back to the agent identifier for subjects with no directory row.
 */
export function useIdentityMetrics(
  identity: IdentityModel,
  from: Date,
  to: Date,
  // Restricts the read to one class of AI account ("team" | "personal"); ""
  // reads every account, which is what every panel but Usage wants.
  accountType = "",
): ReturnType<typeof useGetUserMetricsSummary> {
  const client = useGramContext();
  const { slug: gramProject } = useIdentityProject();
  const userId = identity.userIds[0];
  const externalUserId = identity.externalUserIds[0];
  const request = {
    gramProject,
    getUserMetricsSummaryPayload: {
      from,
      to,
      ...(userId ? { userId } : externalUserId ? { externalUserId } : {}),
      ...(accountType ? { accountType } : {}),
    },
  };

  // Built rather than called through useGetUserMetricsSummary, purely to widen
  // the cache key. This endpoint takes its subject and window in the request
  // body, and the generated key covers only the project and auth headers, so
  // every identity and every range in one project share a single cache entry:
  // the tiles would keep the first answer they got and the range picker would
  // look inert on exactly the figures this page leads with. The generated
  // queryFn still does the fetching; only the key is ours.
  const built = buildGetUserMetricsSummaryQuery(client, request);
  return useQuery({
    ...built,
    queryKey: [
      ...built.queryKey,
      {
        userId: userId ?? null,
        externalUserId: externalUserId ?? null,
        from: from.toISOString(),
        to: to.toISOString(),
        accountType,
      },
    ],
    throwOnError: false,
    enabled: !!userId || !!externalUserId,
  });
}

/**
 * Whether anything in the org has actually been seen under this identity.
 *
 * The resolver answers structurally: hand it any well-formed URN and it returns
 * a subject, so a typo or a stale link renders a complete page for a person who
 * does not exist. A directory row settles it; for everyone else the question is
 * whether telemetry has ever recorded the identifier, which is a targeted
 * lookup rather than the roster crawl the index does.
 */
export function useIdentityIsKnown(identity: IdentityModel | undefined): {
  known: boolean;
  isPending: boolean;
  /**
   * Whether the roster read failed. `known` is false either way, and callers
   * that word that as a finding — "no activity recorded", "not seen" —
   * need to tell the two apart.
   */
  isError: boolean;
  refetch: () => void;
} {
  const client = useGramContext();
  const organization = useOrganization();
  const { slug: projectSlug } = useIdentityProject();
  const hasDirectoryRow = (identity?.userIds.length ?? 0) > 0;
  const identifiers = new Set(
    [...(identity?.emails ?? []), ...(identity?.externalUserIds ?? [])].map(
      (value) => value.toLowerCase(),
    ),
  );
  // The same roster the index lists, under the same query key, so this is a
  // cache hit whenever the reader arrived from there and one fetch otherwise.
  // Asking searchUsers for the identifier directly does not work: the roster
  // surfaces email-keyed identities through the agent-metrics view and
  // id-keyed ones from raw logs, and the id filter matches neither.
  const query = useQuery({
    queryKey: identityRosterQueryKey(organization.id, projectSlug),
    queryFn: () => fetchIdentityRoster(client, projectSlug),
    throwOnError: false,
    enabled: !!identity && !hasDirectoryRow && identifiers.size > 0,
  });

  const outcome = {
    isError: query.isError,
    refetch: () => void query.refetch(),
  };

  if (!identity) return { known: true, isPending: true, ...outcome };
  if (hasDirectoryRow) return { known: true, isPending: false, ...outcome };
  // Only an unattributed subject is the "identifier nothing was recorded
  // under" case: an api-key or agent identity legitimately carries neither an
  // address nor an agent id, and is still a real subject.
  if (identity.kind !== "unattributed")
    return { known: true, isPending: false, ...outcome };
  if (identifiers.size === 0)
    return { known: false, isPending: false, ...outcome };
  if (query.isPending) return { known: false, isPending: true, ...outcome };
  return {
    known: (query.data ?? []).some(
      (summary) =>
        // Independently, not `userEmail || userId`: a row can carry both, and
        // preferring the address there would miss an identity we only know by
        // the id an agent reported — which renders as "never seen here".
        (!!summary.userEmail &&
          identifiers.has(summary.userEmail.toLowerCase())) ||
        (!!summary.userId && identifiers.has(summary.userId.toLowerCase())),
    ),
    isPending: false,
    ...outcome,
  };
}

/**
 * Risk findings and the shadow-MCP inventory are org:admin surfaces on their
 * own pages, and their endpoints enforce that. A reader without it is shown
 * nothing rather than an empty panel that reads as "no findings".
 */
export function useCanReadRisk(): boolean {
  const { hasScope } = useRBAC();
  return hasScope("org:admin");
}

/**
 * Whether the identity on screen is the person reading the page.
 *
 * The directory id settles it. The address only counts for a subject with no
 * directory row at all: another person's identity can list the viewer's
 * address as a linked account, and treating that as "self" would render the
 * viewer's own sessions under someone else's name.
 */
export function useIsSelf(identity: IdentityModel): boolean {
  const { user } = useSession();
  if (identity.userIds.includes(user.id)) return true;
  if (identity.userIds.length > 0) return false;
  return identity.emails.some(
    (email) => email.toLowerCase() === user.email.toLowerCase(),
  );
}

/**
 * Whether this viewer can be shown someone else's sessions at all.
 *
 * chat.list honours an explicit user filter only for a caller who passes its
 * chat:read check for the project being listed; without it the filter is
 * discarded and the caller's OWN sessions come back instead. Rendering those
 * under the subject's name would be a silent misattribution, so the panel asks
 * for nothing rather than asking for something it cannot trust — and the check
 * is made against the project on screen, since a grant scoped to a different
 * project buys nothing here.
 */
export function useCanReadOthersChats(): boolean {
  const { hasAnyScopeInProject } = useRBAC();
  const { id: projectId } = useIdentityProject();
  return hasAnyScopeInProject(["chat:read"], projectId);
}

export function useIdentityChats(
  identity: IdentityModel,
  from: Date,
  to: Date,
  limit = 5,
): ReturnType<typeof useListChats> {
  const { slug: gramProject } = useIdentityProject();
  // Without chat:read the endpoint returns the caller's own sessions whatever
  // filter it is handed — which is exactly right when the subject IS the
  // caller, and a misattribution for anyone else.
  const hasChatRead = useCanReadOthersChats();
  const isSelf = useIsSelf(identity);
  const canReadOthersChats = hasChatRead || isSelf;
  const userId = identity.userIds[0];
  const externalUserId = identity.externalUserIds[0];
  return useListChats(
    {
      ...(userId ? { userId } : {}),
      ...(userId ? {} : externalUserId ? { externalUserId } : {}),
      from,
      to,
      limit,
      gramProject,
    },
    undefined,
    {
      ...OFF,
      enabled: canReadOthersChats && (!!userId || !!externalUserId),
    },
  );
}

export function useIdentityAuditLogs(
  identity: IdentityModel,
  from: Date,
  to: Date,
): ReturnType<typeof useAuditLogs> {
  const actorId = identity.userIds[0];
  return useAuditLogs({ actorId, from, to }, undefined, {
    ...OFF,
    enabled: !!actorId,
  });
}

export type IdentityRisk = {
  data:
    | {
        findings: number;
        categories: RiskOverviewCategory[];
        rules: RiskRuleBreakdownEntry[];
      }
    | undefined;
  isLoading: boolean;
  isError: boolean;
  refetch: () => Promise<unknown>;
};

/**
 * Findings by category and rule across every agent id. The endpoint takes one
 * id; a finding belongs to one id, so summing counts each once.
 */
export function useIdentityRisk(
  identity: IdentityModel,
  from: Date,
  to: Date,
): IdentityRisk {
  const client = useGramContext();
  const { slug: gramProject } = useIdentityProject();
  const canReadRisk = useCanReadRisk();
  return useQueries({
    queries: identity.externalUserIds.map((externalUserId) => ({
      ...buildRiskUserBreakdownQuery(client, {
        externalUserId,
        from,
        to,
        gramProject,
      }),
      ...OFF,
      enabled: canReadRisk,
    })),
    combine: (results) => {
      const loaded = results.flatMap((r) => (r.data ? [r.data] : []));
      return {
        // Only once every id has answered: a sum missing one would pass for
        // the whole count. A failed refresh keeps its earlier data, so a
        // total that went stale still shows, flagged as such.
        data:
          loaded.length > 0 && loaded.length === results.length
            ? sumBreakdowns(loaded)
            : undefined,
        isLoading: results.some((r) => r.isLoading),
        isError: results.some((r) => r.isError),
        // Only the failed reads: see retryFailed.
        refetch: () =>
          Promise.all(results.filter((r) => r.isError).map((r) => r.refetch())),
      };
    },
  });
}

function sumBreakdowns(
  breakdowns: RiskUserBreakdownResult[],
): NonNullable<IdentityRisk["data"]> {
  const categories = new Map<string, RiskOverviewCategory>();
  const rules = new Map<string, RiskRuleBreakdownEntry>();
  for (const b of breakdowns) {
    for (const c of b.categories) {
      const seen = categories.get(c.category);
      categories.set(c.category, {
        ...c,
        findings: (seen?.findings ?? 0) + Number(c.findings),
      });
    }
    for (const r of b.rules) {
      const key = `${r.source}\u0000${r.ruleId}`;
      const seen = rules.get(key);
      rules.set(key, {
        ...r,
        findings: (seen?.findings ?? 0) + Number(r.findings),
      });
    }
  }
  const byFindings = (a: { findings: number }, b: { findings: number }) =>
    b.findings - a.findings;
  return {
    findings: breakdowns.reduce((sum, b) => sum + Number(b.findings), 0),
    categories: [...categories.values()].sort(byFindings),
    rules: [...rules.values()].sort(byFindings),
  };
}

/** Which of an identity's ids the risk panels matched on, for a footer. */
export function riskMatchedOnLabel(externalUserIds: string[]): string {
  if (externalUserIds.length === 0) {
    return "This identity reports no agent identifier, so risk cannot key on it.";
  }
  if (externalUserIds.length === 1) return `Matched on ${externalUserIds[0]}`;
  return `Matched on all ${externalUserIds.length} identifiers this identity reports`;
}

/**
 * The org member row for this identity, matched on the Speakeasy user id and then
 * on any address the subject is known by. It carries the canonical principal
 * URN and the role ids, neither of which the resolver returns.
 */
export function useIdentityMember(identity: IdentityModel): {
  member: AccessMember | undefined;
  query: ReturnType<typeof useMembers>;
} {
  const membersQuery = useMembers(undefined, undefined, OFF);
  const userIds = new Set(identity.userIds);
  const emails = new Set(identity.emails.map((e) => e.toLowerCase()));
  const members = membersQuery.data?.members ?? [];
  // The directory id settles it outright. An address does not: an
  // unattributed subject carries whatever address an agent reported, and more
  // than one member can claim it — an alias, a shared mailbox, a rotated
  // account. Taking the first of several would attach a real person's roles
  // and challenges to an identity that may not be theirs, so an ambiguous
  // address resolves to nobody and the panels say the identity matches no
  // member row.
  const byUserId = members.find((m) => userIds.has(m.id));
  if (byUserId) return { member: byUserId, query: membersQuery };
  const byEmail = members.filter((m) => emails.has(m.email.toLowerCase()));
  return {
    member: byEmail.length === 1 ? byEmail[0] : undefined,
    query: membersQuery,
  };
}

/**
 * The principal challenges and grants are recorded against, which the member
 * row states outright. The Speakeasy user id is the fallback for a subject with no
 * member row: the authz engine mints `user:<speakeasy user id>` principals, so the
 * WorkOS id — which only role ASSIGNMENTS key on — would match no challenge.
 */
export function useIdentityPrincipalUrn(
  identity: IdentityModel,
): string | undefined {
  const { member } = useIdentityMember(identity);
  const fallback = identity.userIds[0];
  return member?.principalUrn ?? (fallback ? `user:${fallback}` : undefined);
}

/**
 * The MCP servers and skills this identity is authorized to reach, through a
 * grant on them or on a role they hold, less any blocking grant withdrawing
 * the same scope. Plugin membership decides what a resource is distributed
 * through, not who may use it, so it does not widen this.
 *
 * The endpoint takes the Speakeasy user id — not the principal URN the panels
 * beside it use — and resolves that user's principals itself inside the active
 * organization. A subject with neither a member row nor a Speakeasy user id has no
 * id to ask about, so the read stays off rather than asking about "".
 */
export function useIdentityAccessibleResources(
  identity: IdentityModel,
): ReturnType<typeof useIdentityAccess> {
  const { member } = useIdentityMember(identity);
  const userId = member?.id ?? identity.userIds[0];
  return useIdentityAccess({ userId: userId ?? "" }, undefined, {
    ...OFF,
    enabled: !!userId,
  });
}

/**
 * Challenges key on the principal URN the authz engine recorded.
 *
 * The returned rows are one capped page, but `total` counts every challenge
 * matching the filter, so a caller that needs an exact count asks for the
 * slice it wants to count rather than counting the page.
 */
export function useIdentityChallenges(
  identity: IdentityModel,
  from: Date,
  to: Date,
  filter: {
    outcome?: "allow" | "deny";
    resolved?: boolean;
    limit?: number;
  } = {},
): ReturnType<typeof useChallenges> {
  const principalUrn = useIdentityPrincipalUrn(identity);
  return useChallenges(
    {
      principalUrn: principalUrn ?? "",
      limit: filter.limit ?? 25,
      outcome: filter.outcome,
      resolved: filter.resolved,
      from,
      to,
    },
    undefined,
    {
      ...OFF,
      enabled: !!principalUrn,
    },
  );
}

export function useIdentityShadowServers(
  identity: IdentityModel,
  from: Date,
  to: Date,
  limit = 10,
): ReturnType<typeof useShadowMCPInventoryServersForUser> {
  const project = useIdentityProject();
  const canReadRisk = useCanReadRisk();
  // Shadow MCP attributes usage to whatever the client reported, which is an
  // address for some agents and an agent-side id for others — pass both sets.
  // The endpoint caps the list at MAX_USER_KEYS and rejects anything longer,
  // and a rejected request renders as an empty panel — "no shadow servers" —
  // which is the one answer we would not have earned. Duplicates go first (an
  // address is commonly both an email and the reported id), then the
  // remainder is bounded so a subject with an unusual number of aliases still
  // gets the reading for the identifiers we could carry.
  const userKeys = [
    ...new Set([...identity.emails, ...identity.externalUserIds]),
  ].slice(0, MAX_USER_KEYS);
  return useShadowMCPInventoryServersForUser(
    { projectId: project.id, userKeys, from, to, limit },
    undefined,
    { ...OFF, enabled: canReadRisk && userKeys.length > 0 },
  );
}

export function useIdentityDevices(
  identity: IdentityModel,
): ReturnType<typeof useManagedDevices> {
  const userIds = identity.userIds;
  const userEmails = identity.emails;
  return useManagedDevices({ userIds, userEmails, limit: 50 }, undefined, {
    ...OFF,
    enabled: userIds.length > 0 || userEmails.length > 0,
  });
}

/**
 * This identity's row in the windowed org-wide roster, plus its peers.
 *
 * One request serves three readings the per-user summary cannot give: where
 * their figures sit against everyone else's, which agent surfaces they work
 * through (hookSources), and whether any linked account is a personal one.
 * The per-user endpoint returns none of those.
 */
export function useIdentityPeers(
  identity: IdentityModel,
  from: Date,
  to: Date,
  // As above: "" is the whole roster, which is what the cached read every
  // other panel shares.
  accountType = "",
): {
  peers: UserSummary[];
  self: UserSummary | undefined;
  isPending: boolean;
  isError: boolean;
  refetch: () => void;
} {
  const client = useGramContext();
  const organization = useOrganization();
  const { slug: projectSlug } = useIdentityProject();
  // An identity known only by the id an agent reported for itself is absent
  // from the internal roster, which groups by Speakeasy user id — it lives under
  // external_user_id instead. Anyone with a directory row or an address is
  // found the usual way; only the pure-agent case switches, and it also puts
  // that agent among agents rather than ranking it against people.
  const userType: IdentityRosterUserType =
    identity.userIds.length === 0 &&
    identity.emails.length === 0 &&
    identity.externalUserIds.length > 0
      ? "external"
      : "internal";
  const query = useQuery({
    queryKey: identityPeersQueryKey(
      organization.id,
      projectSlug,
      from,
      to,
      accountType,
      userType,
    ),
    queryFn: () =>
      fetchIdentityPeers(client, projectSlug, from, to, accountType, userType),
    throwOnError: false,
  });

  const identifiers = new Set(
    [...identity.emails, ...identity.externalUserIds, ...identity.userIds].map(
      (value) => value.toLowerCase(),
    ),
  );
  const rows = query.data ?? [];
  const isSelf = (summary: UserSummary): boolean =>
    identifiers.has((summary.userEmail || "").toLowerCase()) ||
    identifiers.has((summary.userId || "").toLowerCase());

  // The roster keys a row per address, so this identity's addresses arrive as
  // separate rows. Folded into one before anything reads them: apart, they
  // rank as several people, each holding only its own share of the figure the
  // rank qualifies.
  const selfRows = rows.filter(isSelf);
  const self = selfRows.length > 0 ? mergeUserSummaries(selfRows) : undefined;
  const peers = self ? [self, ...rows.filter((row) => !isSelf(row))] : rows;

  return {
    peers,
    self,
    isPending: query.isPending,
    isError: query.isError,
    refetch: () => void query.refetch(),
  };
}
